import {
  validateAlertFrame,
  validateDomainEventFrame,
  validateSettingsFrame,
  validateUserParametersFrame,
  type ActiveCase,
  type AlertFrame,
  type DomainEventFrame,
  type NewCase,
  type SettingsFrame,
  type UserParametersFrame,
} from '#entities';
import { reportThrottled } from '#shared';

const REPORT_THROTTLE_MS = 60_000;

// NOTE: duplicated from CommandCenterMap-BE src/app/ws/updateMessage.ts — keep the
// two in sync until a shared package exists (TSMED-1464 M2).
export interface UpdateMessage {
  changed: string[];
  ids?: Record<string, string[]>;
  ts: number;
}

/** True only for a plain object whose every value is a string[]. */
const isStringArrayRecord = (
  value: unknown,
): value is Record<string, string[]> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    entry =>
      Array.isArray(entry) && entry.every(item => typeof item === 'string'),
  );

/**
 * Parse a message pushed by the BE update gateway (`{changed,ids,ts}`).
 * Returns null for anything malformed so a bad frame can never crash the app.
 */
export const parseServerMessage = (raw: string): UpdateMessage | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const { changed, ids, ts } = parsed as Record<string, unknown>;

  if (!Array.isArray(changed) || changed.length === 0) {
    return null;
  }
  if (!changed.every(entry => typeof entry === 'string')) {
    return null;
  }

  const message: UpdateMessage = {
    changed: changed as string[],
    ts: typeof ts === 'number' ? ts : 0,
  };
  // Validate ids like changed; a malformed ids is dropped (never cast blindly)
  // so it can't reach consumers, while a valid `changed` still gets through.
  if (isStringArrayRecord(ids)) {
    message.ids = ids;
  }
  return message;
};

// NOTE: duplicated from CommandCenterMap-BE src/app/ws/updateMessage.ts
// (`EntityChangeEventFrame`, `EntityFrame`) — keep in sync (TSMED-1464 M2).

/**
 * Entity-touch trigger pushed by the BE events channel: WHICH case changed and
 * WHEN the BE learned of it.
 *
 * TMDXNC-257: the four field-level keys are OPTIONAL. The BE is narrowing this
 * frame to `entityId` + `detectedAt` — nothing reads the deltas (the
 * `entityChangeEventBus` has no consumers, and the entity projector re-fetches
 * authoritative state), and the SQS source it now comes from cannot produce them
 * (`CaseDetailsUpdated` is a whole-case PATCH, not a delta).
 *
 * Optional rather than removed, deliberately: this parser must accept BOTH
 * shapes, and it must ship BEFORE the BE stops sending the wide one. A BE-first
 * deploy would have every frame rejected here and live sync would die silently.
 * Once the BE narrowing is deployed everywhere, these four can go.
 */
export interface EntityChangeEventFrame {
  kind: 'event';
  entityType: 'case';
  entityId: string;
  changeType?: 'created' | 'updated';
  propertyName?: string;
  previousValue?: unknown;
  value?: unknown;
  /** Arrival-time watermark (ISO 8601) the BE draws from `nop_case_event.receivedAt`. */
  detectedAt: string;
  ts: number;
}

/** A ready-to-use FE entity pushed by the BE entity channel. */
export interface EntityFrame {
  kind: 'entity';
  entityType: 'case';
  entityId: string;
  list: 'active' | 'new' | null;
  entity: ActiveCase | NewCase | null;
  removed?: boolean;
  ts: number;
  cursor: string;
}

// NOTE: duplicated from CommandCenterMap-BE src/app/ws/updateMessage.ts
// (`PresenceFrame`) — keep in sync (TMDXNC-241).

/**
 * Full snapshot of the users currently online, by Cognito oid. Admin-only:
 * the BE fans it out solely to admin sockets, and one arrives immediately on
 * connect. Snapshot semantics — consumers replace the online set wholesale,
 * never merge (applyPresenceFrame).
 */
export interface PresenceFrame {
  kind: 'presence';
  onlineOids: string[];
  ts: number;
}

/** Anything the gateway may broadcast to clients. */
export type InboundFrame =
  | UpdateMessage
  | EntityChangeEventFrame
  | EntityFrame
  | DomainEventFrame
  | PresenceFrame
  | AlertFrame
  | SettingsFrame
  | UserParametersFrame;

const asRecord = (raw: string): Record<string, unknown> | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return typeof parsed === 'object' && parsed !== null
    ? (parsed as Record<string, unknown>)
    : null;
};

const parseEventFrame = (
  p: Record<string, unknown>,
): EntityChangeEventFrame | null => {
  if (
    p.entityType !== 'case' ||
    typeof p.entityId !== 'string' ||
    p.entityId.length === 0
  ) {
    return null;
  }
  // TMDXNC-257: the field-level keys are carried when present and simply absent
  // otherwise. Neither case is a reason to reject the frame — its job is to say
  // which case to refresh. They are omitted rather than defaulted to null so a
  // consumer can tell "the BE sent no delta" from "the delta was null".
  const frame: EntityChangeEventFrame = {
    kind: 'event',
    entityType: 'case',
    entityId: p.entityId,
    detectedAt: typeof p.detectedAt === 'string' ? p.detectedAt : '',
    ts: typeof p.ts === 'number' ? p.ts : 0,
  };
  if (p.changeType === 'created' || p.changeType === 'updated') {
    frame.changeType = p.changeType;
  }
  if (typeof p.propertyName === 'string') {
    frame.propertyName = p.propertyName;
    frame.previousValue = p.previousValue ?? null;
    frame.value = p.value ?? null;
  }
  return frame;
};

const parseEntityFrameInternal = (
  p: Record<string, unknown>,
): EntityFrame | null => {
  if (
    p.entityType !== 'case' ||
    typeof p.entityId !== 'string' ||
    p.entityId.length === 0
  ) {
    return null;
  }
  if (p.list !== 'active' && p.list !== 'new' && p.list !== null) {
    return null;
  }
  return {
    kind: 'entity',
    entityType: 'case',
    entityId: p.entityId,
    list: p.list,
    entity: (p.entity ?? null) as ActiveCase | NewCase | null,
    removed: p.removed === true,
    ts: typeof p.ts === 'number' ? p.ts : 0,
    cursor: typeof p.cursor === 'string' ? p.cursor : '',
  };
};

/**
 * Validates one wire item into an `EntityFrame`, or `null`. Shared by the live WS path
 * and `caseCatchup.ts`'s `runCaseCatchup` so both apply identical per-item checks.
 */
export const validateEntityFrame = (value: unknown): EntityFrame | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return parseEntityFrameInternal(value as Record<string, unknown>);
};

const parsePresenceFrameInternal = (
  p: Record<string, unknown>,
): PresenceFrame | null => {
  const { onlineOids, ts } = p;
  if (
    !Array.isArray(onlineOids) ||
    !onlineOids.every(oid => typeof oid === 'string') ||
    typeof ts !== 'number'
  ) {
    return null;
  }
  return { kind: 'presence', onlineOids: onlineOids as string[], ts };
};

interface BadFrameDetail {
  entriesField?: string;
  issuePaths?: string[];
}

/**
 * A frame that self-identifies by `kind` but fails validation is a
 * contract-drift signal — report it (throttled) distinctly from the
 * generic unparseable-JSON drop, which returns null with no kind.
 * Only the frame's shape is sent: a userParameters frame carries one user's
 * values.
 */
const reportBadFrame = (
  slug: string,
  kind: string,
  frame: Record<string, unknown>,
  { entriesField, issuePaths }: BadFrameDetail = {},
): void => {
  const entries = entriesField === undefined ? undefined : frame[entriesField];
  reportThrottled(
    `nop-events:bad-${slug}-frame`,
    `nop-events: ${kind} frame failed validation`,
    {
      throttleMs: REPORT_THROTTLE_MS,
      level: 'warning',
      extra: {
        kind,
        keys: Object.keys(frame),
        ...(Array.isArray(entries) ? { entryCount: entries.length } : {}),
        ...(issuePaths ? { issuePaths } : {}),
      },
    },
  );
};

/**
 * Parses any inbound frame by `kind`; no `kind` is treated as the legacy `{changed,ids,ts}`
 * shape. Returns null for anything malformed so a bad frame can never crash the app.
 */
export const parseInboundFrame = (raw: string): InboundFrame | null => {
  const p = asRecord(raw);
  if (!p) {
    return null;
  }
  if (p.kind === 'event') {
    return parseEventFrame(p);
  }
  if (p.kind === 'entity') {
    return parseEntityFrameInternal(p);
  }
  if (p.kind === 'presence') {
    const frame = parsePresenceFrameInternal(p);
    if (!frame) {
      reportBadFrame('presence', 'presence', p, { entriesField: 'onlineOids' });
    }
    return frame;
  }
  if (p.kind === 'domainEvent') {
    const frame = validateDomainEventFrame(p);
    if (!frame) {
      reportBadFrame('domain-event', 'domainEvent', p);
    }
    return frame;
  }
  if (p.kind === 'alert') {
    const frame = validateAlertFrame(p);
    if (!frame) {
      reportBadFrame('alert', 'alert', p);
    }
    return frame;
  }
  if (p.kind === 'settings') {
    return validateSettingsFrame(p, issuePaths =>
      reportBadFrame('settings', 'settings', p, {
        entriesField: 'settings',
        issuePaths,
      }),
    );
  }
  if (p.kind === 'userParameters') {
    return validateUserParametersFrame(p, issuePaths =>
      reportBadFrame('user-parameters', 'userParameters', p, {
        entriesField: 'parameters',
        issuePaths,
      }),
    );
  }
  return parseServerMessage(raw);
};
